<template>
  <div class="mobile-employee-select">
    <span class="text" :class="{placeholder: !showText}" @click="toShowDrawer">{{ showText || placeholder }}</span>
    <!--
      抽屉头部：清空 - 标题 - 确认，与 MobileSearchSelect 同一套。
      ★ 左边原本是「取消」，但点遮罩本身就是取消，这个按钮是多余的；改成「清空」更有用 ——
        清空已选并直接生效，省得一个个取消勾选。
    -->
    <mobile-drawer v-model="showDrawer" :title="title" cancel-text="清空"
                   @confirm="confirmSelect" @cancel="clearSelect">
      <div class="employee-select-drawer">
        <mobile-list-header>
          <div class="search-bar">
            <div class="search-input">
              <yq-icon class="icon" name="element-ui_search"/>
              <input v-model="searchInfo.searchKey" placeholder="职员名称/手机号" @keyup.enter="resetSearch"/>
            </div>
            <!--
              ★ 必须有这个显式的搜索按钮：手机上光靠 input 的回车不靠谱 ——
                中文输入法打字时按回车是「上屏」、被输入法自己吃掉，走不到 keyup.enter。
            -->
            <div class="search-btn" @click="resetSearch">搜索</div>
          </div>
        </mobile-list-header>
        <mobile-list :loading="loading" :has-more="searchInfo.hasMore" :query-list="queryList">
          <!-- 职员行：头像 + 名称/手机号两行（预览模板 B） -->
          <div class="employee-item" v-for="item in employeeList" :key="item.id" @click="toggle(item)">
            <div class="employee-avatar">
              <img v-if="item.iconUrl" :src="$getImageUrl(item.iconUrl)" alt=""/>
              <yq-icon v-else name="element-ui_user"/>
            </div>
            <div class="employee-info">
              <span class="employee-name">{{ item.employeeName }}</span>
              <span class="employee-phone">{{ item.employeePhone || "无联系电话" }}</span>
            </div>
            <yq-icon v-if="isSelected(item)" class="employee-check" name="element-ui_check"/>
          </div>
        </mobile-list>
      </div>
    </mobile-drawer>
  </div>
</template>

<script>

import YqIcon from "@/components/YqIcon.vue";
import MobileDrawer from "@/components/qywx/data/MobileDrawer.vue";
import MobileList from "@/components/qywx/layout/MobileList.vue";
import MobileListHeader from "@/components/qywx/layout/MobileListHeader.vue";
import commonApi from "@/api/commonApi";

export default {
  emits: ["update:modelValue", "change"],
  props: {
    modelValue: {default: ""},     // 负责人id串，逗号分隔，第一位是主要负责人
    displayText: {default: ""},    // 外部回显的姓名串（与 modelValue 同序），有值时优先用它
    placeholder: {default: "请选择"},
    title: {default: "选择负责人"},
  },
  name: "MobileEmployeeSelect",
  components: {
    YqIcon, MobileDrawer, MobileList, MobileListHeader
  },
  data() {
    return {
      showDrawer: false,
      // [{id, employeeName}]，顺序即优先级，第一位是主要负责人。
      // 已提交的那份（回显用），只在点「确认 / 清空」时变
      selectedEmployees: [],
      // 抽屉里正在勾的那份。抽屉一开就从已选拷一份，勾选只动它 ——
      // 这样点遮罩取消时勾选不会留下来（原来直接改已选，取消等于没取消）
      pendingEmployees: [],
      searchInfo: {
        searchKey: "",
        pageNumber: 0,
        pageSize: 20,
        hasMore: true,
      },
      loading: false,
      employeeList: [],
    };
  },
  computed: {
    showText() {
      if (this.displayText) {
        return this.displayText.split(",").filter(v => v).join("、");
      }
      return this.selectedEmployees.map(item => item.employeeName).filter(name => name).join("、");
    }
  },
  methods: {
    // 打开抽屉
    toShowDrawer() {
      // 首次打开：用外部回显的 id 串 / 姓名串初始化已选（两者同序）
      if (this.selectedEmployees.length === 0) {
        const ids = (this.modelValue || "").split(",").filter(v => v);
        const names = (this.displayText || "").split(",");
        this.selectedEmployees = ids.map((id, index) => ({id, employeeName: names[index] || ""}));
      }
      // 抽屉里的待选从已选拷一份，勾选只动待选
      this.pendingEmployees = this.selectedEmployees.slice();
      this.showDrawer = true;
      this.resetSearch();
    },
    isSelected(item) {
      return this.pendingEmployees.some(employee => employee.id === item.id);
    },
    toggle(item) {
      const index = this.pendingEmployees.findIndex(employee => employee.id === item.id);
      if (index > -1) {
        this.pendingEmployees.splice(index, 1);
      } else {
        this.pendingEmployees.push({id: item.id, employeeName: item.employeeName});
      }
    },
    confirmSelect() {
      this.showDrawer = false;
      this.selectedEmployees = this.pendingEmployees.slice();
      this.$emit("update:modelValue", this.selectedEmployees.map(item => item.id).join(","));
      this.$emit("change", this.selectedEmployees);
    },
    /**
     * 清空：把已选也一并清掉并立即生效（抽屉由 header 的取消动作关掉）。
     * 与「点遮罩取消」的区别就在这 —— 取消只丢待选，已选不动。
     */
    clearSelect() {
      this.pendingEmployees = [];
      this.selectedEmployees = [];
      this.$emit("update:modelValue", "");
      this.$emit("change", []);
    },
    resetSearch() {
      this.searchInfo.searchKey = "";
      this.searchInfo.pageNumber = 0;
      this.searchInfo.hasMore = true;
      this.employeeList = [];
      this.queryList();
    },
    queryList() {
      this.loading = true;
      this.searchInfo.pageNumber++;
      commonApi.queryEmployeeListByPage(this.searchInfo).then(res => {
        this.employeeList = this.employeeList.concat(res.records || []);
        this.searchInfo.hasMore = res.pageCount > this.searchInfo.pageNumber;
        this.loading = false;
      }).catch(() => {
        this.loading = false;
      })
    }
  }
};
</script>

<style lang="scss" scoped>
.mobile-employee-select{
  // 触发区：纯文字、无背景色（和 MobileSearchSelect / MobileConditionSelect 保持一致）
  .text{
    color: var(--yq-font-color-base);
    &.placeholder{
      color: var(--yq-font-color-desc);
    }
  }
}
.employee-select-drawer{
  height: 100%;
  display: flex;
  flex-direction: column;
  :deep(.mobile-list-header){
    flex: none;
  }
  // 搜索行：左边输入框、右边「搜索」按钮，与列表页的搜索栏同一套观感
  .search-bar{
    display: flex;
    align-items: center;
    height: 44px;
    padding-left: 12px;
    border-radius: 999px;
    background: var(--yq-background-color);
    overflow: hidden;
    .search-input{
      flex: 1;
      display: flex;
      align-items: center;
      min-width: 0;
      height: 100%;
      padding-right: 12px;
      .icon{
        flex: none;
        color: var(--yq-font-color-desc);
        font-size: var(--yq-font-size-mini-mobile);
      }
      input{
        flex: 1;
        min-width: 0;
        height: 100%;
        margin-left: 6px;
        border: none;
        outline: none;
        background: transparent;
        font-size: var(--yq-font-size-mini-mobile);
        color: var(--yq-font-color-base);
        &::placeholder{
          color: var(--yq-font-color-desc);
        }
      }
    }
    .search-btn{
      flex: none;
      display: flex;
      align-items: center;
      height: 100%;
      padding: 0 18px;
      background: var(--el-color-primary);
      color: #FFF;
      font-size: var(--yq-font-size-mini-mobile);
    }
  }
  .employee-item{
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #FFF;
    margin-top: 10px;
    padding: 15px;
    border-radius: 6px;
    box-shadow: 0 0 10px var(--yq-shadow);
    // 头像：预览模板 B 那一款（有头像显示头像，没有就一个灰色的人形图标）
    .employee-avatar{
      flex: none;
      width: 40px;
      height: 40px;
      margin-right: 10px;
      border-radius: 8px;
      background: var(--yq-background-color);
      color: #b9c2cf;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      img{
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
    .employee-info{
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      .employee-name{
        font-size: var(--yq-font-size-mini-mobile);
        color: var(--yq-font-color-base);
      }
      .employee-phone{
        margin-top: 4px;
        font-size: var(--yq-font-size-mini-mobile);
        color: var(--yq-font-color-desc);
      }
    }
    .employee-check{
      flex: none;
      color: var(--el-color-primary);
    }
  }
}
</style>
